import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import { motion, AnimatePresence } from 'framer-motion';
import { AiOutlineClose } from 'react-icons/ai';
import { MdCall } from 'react-icons/md';
import { FaInstagram } from 'react-icons/fa';
import { MapPin } from 'lucide-react';
import AppointmentModal from "../components/appointmentModal/AppointmentModal.js";
import styles from './navbar.module.css';

const Navbar = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const messages = [
    "Escape into Nature",
    "Book Your Camp Today",
    "Adventure, Comfort, Serenity",
  ];

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about-us' },
    { label: 'Campsite Map View', path: '/campsite-map-view' },
    { label: 'Our Tents', path: '/our-tents' },
    { label: 'Contact Us', path: '/contact-us' },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % messages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [messages.length]);

  return (
    <>
      {/* Announcement Topbar Banner */}
      <div className={styles.topbar}>
        <div className={styles.topbarInner}>
          <div className={styles.announcementColumn}>
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ y: 15, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -15, opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className={styles.topbarText}
              >
                {messages[index]}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className={styles.topbarActions}>
            <a
              href="https://www.instagram.com/dima__niwas__campsite/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.topbarLink}
              aria-label="Instagram"
            >
              <FaInstagram size={15} />
            </a>
            <a
              href="https://share.google/5uxbeiPnhyjxUPt4f"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.topbarBtn}
            >
              <MapPin size={13} />
              <span>Location</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={styles.navbar}>
        <div className={styles.navbarInner}>
          {/* Logo */}
          <Link to="/" className={styles.logoLink}>
            <img src={logo} alt="Logo" className={styles.logoImg} />
          </Link>

          {/* Center Nav Link Items (Desktop) */}
          {!isMobile && (
            <div className={styles.navLinksContainer}>
              {navLinks.map(({ label, path }, idx) => {
                const isActive = location.pathname === path;
                return (
                  <Link
                    key={idx}
                    to={path}
                    className={styles.navLink}
                    style={{ color: isActive ? '#267444' : '#4a5568' }}
                  >
                    {label}
                    {isActive && (
                      <motion.div
                        layoutId="activeUnderline"
                        className={styles.activeUnderline}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Right Action buttons (Desktop) */}
          {!isMobile ? (
            <div className={styles.actionsContainer}>
              <button
                onClick={() => setIsModalOpen(true)}
                className={styles.bookBtn}
              >
                Book Your Camp Now
              </button>

              {/* Booking Modal */}
              <AppointmentModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
              />
            </div>
          ) : (
            /* Mobile Menu Toggle Button */
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={styles.mobileToggle}
              aria-label="Toggle Menu"
            >
              <div className={styles.bar} style={{ transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }}></div>
              <div className={styles.bar} style={{ opacity: menuOpen ? 0 : 1 }}></div>
              <div className={styles.bar} style={{ transform: menuOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'none' }}></div>
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Drawer Navigation with backdrop blur overlay */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop Blur Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className={styles.drawerOverlay}
            />

            {/* Sidebar Drawer Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
              className={styles.mobileDrawer}
            >
              <div className={styles.drawerHeader}>
                <img src={logo} alt="Logo" className={styles.drawerLogo} />
                <button
                  onClick={() => setMenuOpen(false)}
                  className={styles.closeBtn}
                  aria-label="Close Menu"
                >
                  <AiOutlineClose size={18} />
                </button>
              </div>

              {/* Mobile links list */}
              <ul className={styles.mobileLinks}>
                {navLinks.map(({ label, path }, idx) => {
                  const isActive = location.pathname === path;
                  return (
                    <li key={idx}>
                      <Link
                        to={path}
                        className={`${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ''}`}
                        onClick={() => setMenuOpen(false)}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Call Booking CTA inside menu */}
              <a
                href="tel:+919435851141"
                className={styles.mobileCallBtn}
              >
                <div className={styles.mobileCallIconBox}>
                  <MdCall size={18} />
                </div>
                <div>
                  <div className={styles.mobileCallLabel}>Call to Book</div>
                  <div className={styles.mobileCallNumber}>+91 9435851141</div>
                </div>
              </a>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;